import { TDeserializedKlStorageProject, TKlProject, TKlProjectMeta, TRawMeta } from '../kl-types';
import {
    PROJECT_STORE_THUMBNAIL_SIZE_PX,
    ProjectConverter,
    TKlStorageProjectRead,
    TKlStorageProjectWrite,
} from './project-converter';
import { LocalStorage } from '../../bb/base/local-storage';
import { BROWSER_STORAGE_STORE, IMAGE_DATA_STORE, KL_INDEXED_DB } from './kl-indexed-db';
import { isBlob, randomUuid } from '../../bb/base/base';
import { TIdb } from './kl-indexed-db.types';
import { BB } from '../../bb/bb';
import { canvasToBlob } from '../../bb/base/canvas';

export type TImageDataReference = {
    id: string;
};

export function isTImageDataReference(input: unknown): input is TImageDataReference {
    return (
        typeof input === 'object' &&
        input !== null &&
        'id' in input &&
        typeof (input as any).id === 'string'
    );
}

async function createFallbackThumbnail(): Promise<Blob> {
    return await canvasToBlob(
        BB.canvas(PROJECT_STORE_THUMBNAIL_SIZE_PX, PROJECT_STORE_THUMBNAIL_SIZE_PX),
        'image/png',
    );
}

export class GalleryStore {
    private isAvailable: boolean = true;

    private async lowLevelStore(project: TKlStorageProjectWrite): Promise<void> {
        // We do *not* automatically delete old projects here because we are not overwriting a single slot.
        // But if we are overwriting a specific ID, we should clean up its *old* image data.
        // For simplicity in V1, we will just assume "store" adds/updates.
        // Proper cleanup of orphaned blobs is tricky without a reference count or explicit delete of old version.
        // Let's implement delete-old-blob logic relevant to THIS project ID.

        const projectId = project.id; // The wrapper uses 'id' which maps to keyPath.

        let deleteIds: string[] = [];
        try {
            const rawOld = (await KL_INDEXED_DB.get(BROWSER_STORAGE_STORE, projectId)) as
                | TKlStorageProjectWrite
                | undefined;

            if (rawOld) {
                if (isTImageDataReference(rawOld.thumbnail)) {
                    deleteIds.push(rawOld.thumbnail.id);
                }
                for (const layer of rawOld.layers) {
                    if (isTImageDataReference(layer.blob)) {
                        deleteIds.push(layer.blob.id);
                    }
                }
            }
        } catch (e) {
            // ignore if read fails
        }

        // prepare for storing
        const imageDataList: { id: string; data: Blob }[] = [];
        const thumbnail = {
            id: randomUuid(),
        };
        imageDataList.push({
            id: thumbnail.id,
            data: project.thumbnail!,
        });
        const layers: TIdb['V2']['ProjectStore']['Write']['layers'] = [];
        for (const layer of project.layers) {
            const blob = {
                id: randomUuid(),
            };
            imageDataList.push({
                id: blob.id,
                data: layer.blob,
            });
            layers.push({
                ...layer,
                blob,
            });
        }
        const raw: TIdb['V2']['ProjectStore']['Write'] = {
            ...project,
            thumbnail,
            layers,
        };

        // store first
        for (const imageData of imageDataList) {
            await KL_INDEXED_DB.set(IMAGE_DATA_STORE, imageData.id, imageData.data);
        }

        // Store with undefined key because keyPath 'id' is in 'raw'
        await KL_INDEXED_DB.set(BROWSER_STORAGE_STORE, undefined, raw);

        // remove obsolete imageData
        for (const id of deleteIds) {
            await KL_INDEXED_DB.remove(IMAGE_DATA_STORE, id);
        }
    }

    private async lowLevelRead(id: string): Promise<TKlStorageProjectRead | undefined> {
        const raw = (await KL_INDEXED_DB.get(
            BROWSER_STORAGE_STORE,
            id,
        )) as TIdb['V2']['ProjectStore']['Read'];
        if (!raw) {
            return undefined;
        }

        let thumbnail: Blob | undefined;
        if (isTImageDataReference(raw.thumbnail)) {
            const thumbnailReadResult = (await KL_INDEXED_DB.get(
                IMAGE_DATA_STORE,
                raw.thumbnail.id,
            )) as TIdb['V2']['ImageDataStore']['Read'] | undefined;
            if (isBlob(thumbnailReadResult)) {
                thumbnail = thumbnailReadResult;
            }
        } else {
            thumbnail = raw.thumbnail;
        }
        thumbnail = thumbnail ?? (await createFallbackThumbnail());

        const layers: TKlStorageProjectRead['layers'] = [];
        for (const layer of raw.layers) {
            let blob: Blob | undefined;
            if (isTImageDataReference(layer.blob)) {
                const readResult = (await KL_INDEXED_DB.get(IMAGE_DATA_STORE, layer.blob.id)) as
                    | TIdb['V2']['ImageDataStore']['Read']
                    | undefined;
                if (isBlob(readResult)) {
                    blob = readResult;
                }
            } else {
                blob = layer.blob;
            }
            layers.push({
                ...layer,
                isVisible: layer.isVisible ?? true,
                mixModeStr: layer.mixModeStr ?? 'source-over',
                blob,
            });
        }
        return {
            ...raw,
            projectId: raw.projectId ?? randomUuid(),
            thumbnail,
            layers,
        };
    }

    // ----------------------------------- public -----------------------------------

    constructor() {
        if (!KL_INDEXED_DB.getIsAvailable()) {
            this.isAvailable = false;
        }
    }

    // List all projects (metadata only)
    async getProjectList(): Promise<TKlProjectMeta[]> {
        if (!this.isAvailable) { return []; }

        try {
            const rawList = await KL_INDEXED_DB.getAll(BROWSER_STORAGE_STORE) as TIdb['V2']['ProjectStore']['Read'][];
            const result: TKlProjectMeta[] = [];

            // We need to resolve thumbnails for each (or maybe just return ids/names for list?)
            // Loading all thumbnails might be heavy if they are blobs in another store?
            // Actually, ProjectStore structure has `thumbnail` as {id} or Blob.
            // If it is {id}, we need to fetch it. Fetching ALL thumbnails might be slow.
            // But for a gallery, we need them.

            for (const raw of rawList) {
                let thumbnail: Blob | undefined;
                if (isTImageDataReference(raw.thumbnail)) {
                    const thumbBlob = (await KL_INDEXED_DB.get(IMAGE_DATA_STORE, raw.thumbnail.id)) as Blob | undefined;
                    if (isBlob(thumbBlob)) {
                        thumbnail = thumbBlob;
                    }
                } else {
                    thumbnail = raw.thumbnail;
                }
                thumbnail = thumbnail ?? (await createFallbackThumbnail());

                // Use raw.id (the keyPath) as projectId so it matches when we call read(id)
                result.push({
                    projectId: '' + raw.id, // This is the IndexedDB key we need for reading
                    timestamp: raw.timestamp,
                    thumbnail,
                    name: raw.name, // Include project name if set
                });
            }

            // Sort by timestamp desc
            result.sort((a, b) => b.timestamp - a.timestamp);

            return result;

        } catch (e) {
            console.error('Failed to get gallery list', e);
            return [];
        }
    }

    async store(project: TKlProject, projectName?: string): Promise<void> {
        if (!this.isAvailable) { throw new Error('DB not available'); }
        try {
            const storageProject = await ProjectConverter.createStorageProject(project);
            // ALWAYS generate a new ID for gallery saves - each save is a new snapshot
            storageProject.id = randomUuid();
            if (projectName) {
                storageProject.name = projectName;
            }

            await this.lowLevelStore(storageProject);
        } catch (e) {
            this.isAvailable = false;
            throw new Error('db-error: ' + e);
        }
    }

    async read(id: string): Promise<TDeserializedKlStorageProject | undefined> {
        if (!this.isAvailable) { throw new Error('DB not available'); }
        let storageProject: TKlStorageProjectRead | undefined;
        try {
            storageProject = await this.lowLevelRead(id);
        } catch (e) {
            this.isAvailable = false;
            throw new Error('db-error: ' + e);
        }
        if (!storageProject) {
            return undefined;
        }
        let result;
        try {
            result = await ProjectConverter.readStorageProject(storageProject);
        } catch (e) {
            throw new Error('format-error: ' + e);
        }
        return result;
    }

    async deleteProject(id: string): Promise<void> {
        if (!this.isAvailable) { throw new Error('DB not available'); }
        // We need to delete the project AND its blobs
        // So we read it first to get blob IDs
        try {
            const rawOld = (await KL_INDEXED_DB.get(BROWSER_STORAGE_STORE, id)) as
                | TKlStorageProjectWrite
                | undefined;

            const deleteIds: string[] = [];
            if (rawOld) {
                if (isTImageDataReference(rawOld.thumbnail)) {
                    deleteIds.push(rawOld.thumbnail.id);
                }
                for (const layer of rawOld.layers) {
                    if (isTImageDataReference(layer.blob)) {
                        deleteIds.push(layer.blob.id);
                    }
                }
            }

            await KL_INDEXED_DB.remove(BROWSER_STORAGE_STORE, id);
            for (const blobId of deleteIds) {
                await KL_INDEXED_DB.remove(IMAGE_DATA_STORE, blobId);
            }

        } catch (e) {
            console.error('Failed to delete project', e);
            throw e;
        }
    }

    async updateProjectName(id: string, newName: string): Promise<void> {
        if (!this.isAvailable) { throw new Error('DB not available'); }

        try {
            // Read existing project metadata from store
            const raw = await KL_INDEXED_DB.get(BROWSER_STORAGE_STORE, id) as TKlStorageProjectWrite | undefined;

            if (!raw) {
                throw new Error('Project not found');
            }

            // Update name field (raw already has 'id' property from keyPath)
            (raw as any).name = newName;

            // Save back to IndexedDB using undefined key (store uses in-line keyPath 'id')
            await KL_INDEXED_DB.set(BROWSER_STORAGE_STORE, undefined, raw);
        } catch (e) {
            console.error('Failed to update project name:', e);
            this.isAvailable = false;
            throw new Error('db-error: ' + e);
        }
    }

    getIsAvailable(): boolean {
        this.isAvailable = this.isAvailable && KL_INDEXED_DB.getIsAvailable();
        return this.isAvailable;
    }
}
